import type { OfferHistoryComparisonPoint } from "./offer-history-comparison";

export type HistoryWindowId = "30d" | "90d" | "all";

const DAY = 86_400_000;
const WINDOW_DAYS = { "30d": 30, "90d": 90 } as const;
const NICE_STEPS = [1, 2, 2.5, 5];
const MAX_TICKS = 5;
/** A price within this share of the window's low or high reads as "near" it. */
const NEAR_EXTREME = 0.05;

/** Windows that add data over the next shorter one; none when everything fits in 30 days. */
export function availableWindows(
  points: OfferHistoryComparisonPoint[],
  endTime: number,
): HistoryWindowId[] {
  const first = points[0]?.time;
  if (first === undefined) return [];
  const span = endTime - first;
  if (span <= WINDOW_DAYS["30d"] * DAY) return [];
  if (span <= WINDOW_DAYS["90d"] * DAY) return ["30d", "all"];
  return ["30d", "90d", "all"];
}

export function sliceHistoryWindow(
  points: OfferHistoryComparisonPoint[],
  window: HistoryWindowId,
  endTime: number,
): OfferHistoryComparisonPoint[] {
  if (window === "all") return points;
  const start = endTime - WINDOW_DAYS[window] * DAY;
  const inWindow = points.filter((point) => point.time > start);
  const carried = points.findLast((point) => point.time <= start);
  return carried ? [{ ...carried, time: start }, ...inWindow] : inWindow;
}

/** One point per calendar day: the day's last available price, or a gap when it had none. */
export function bucketDailyPoints(
  points: OfferHistoryComparisonPoint[],
  dayKey: (time: number) => string,
): OfferHistoryComparisonPoint[] {
  const days = new Map<string, OfferHistoryComparisonPoint[]>();
  for (const point of points) {
    const key = dayKey(point.time);
    const day = days.get(key);
    if (day) day.push(point);
    else days.set(key, [point]);
  }
  return [...days.values()].map((dayPoints) => {
    const available = dayPoints.findLast((point) => point.minimum !== null);
    const selected = dayPoints.findLast((point) => point.selected !== null)?.selected ?? null;
    const base = available ?? dayPoints.at(-1)!;
    return { ...base, selected, time: dayPoints[0]!.time };
  });
}

/** A padded domain with 4–5 gridlines at round steps (1, 2, 2.5 or 5 × 10ⁿ). */
export function priceAxisTicks(low: number, high: number) {
  const pad = high > low ? (high - low) * 0.06 : Math.max(high * 0.05, 1);
  const min = Math.max(0, low - pad);
  const max = high + pad;
  const exponent = Math.floor(Math.log10(max - min)) - 1;
  // Cents on the axis add noise, not information: steps stop at one whole unit.
  const steps = [0, 1, 2]
    .flatMap((offset) => NICE_STEPS.map((base) => base * 10 ** (exponent + offset)))
    .filter((step) => step >= 1);
  const axes = steps.map((step) => axisForStep(min, max, step));
  return axes.find((axis) => axis.ticks.length <= MAX_TICKS) ?? axisForStep(min, max, 1);
}

/** A range too narrow for a few gridlines widens out to the step's boundaries. */
function axisForStep(min: number, max: number, step: number) {
  const ticks = tickValues(min, max, step);
  if (ticks.length >= 3) return { min, max, ticks };
  const low = Math.max(0, Math.floor(min / step) * step);
  const high = Math.ceil(max / step) * step;
  return { min: low, max: high, ticks: tickValues(low, high, step) };
}

function tickValues(min: number, max: number, step: number) {
  const ticks: number[] = [];
  for (let value = Math.ceil(min / step) * step; value <= max; value += step) {
    ticks.push(Number(value.toFixed(6)));
  }
  return ticks;
}

export function windowExtremes(points: OfferHistoryComparisonPoint[]) {
  const priced = points.flatMap((point) =>
    point.minimum === null ? [] : [{ time: point.time, price: point.minimum }],
  );
  return {
    low: priced.reduce<(typeof priced)[number] | null>(
      (lowest, item) => (lowest === null || item.price < lowest.price ? item : lowest),
      null,
    ),
    high: priced.reduce<(typeof priced)[number] | null>(
      (highest, item) => (highest === null || item.price > highest.price ? item : highest),
      null,
    ),
    average: priceSummary(points, "minimum").average,
  };
}

export function pricePosition(
  current: number | null,
  low: number | null,
  high: number | null,
): "near_low" | "near_high" | "none" {
  if (current === null || low === null || high === null) return "none";
  if (current <= low * (1 + NEAR_EXTREME)) return "near_low";
  if (current >= high * (1 - NEAR_EXTREME)) return "near_high";
  return "none";
}

/** Min, max and the time-weighted average of one series. */
export function priceSummary(points: OfferHistoryComparisonPoint[], key: "minimum" | "selected") {
  const prices = points.flatMap((point) => (point[key] === null ? [] : [point[key]]));
  const totals = points.reduce(
    (result, point, index) => {
      const price = point[key];
      if (price === null) return result;
      const duration = (points[index + 1]?.time ?? point.time) - point.time;
      return {
        amount: result.amount + price * duration,
        duration: result.duration + duration,
      };
    },
    { amount: 0, duration: 0 },
  );
  const average = totals.duration > 0 ? totals.amount / totals.duration : (prices[0] ?? null);
  return {
    minimum: prices.length ? Math.min(...prices) : null,
    maximum: prices.length ? Math.max(...prices) : null,
    average,
  };
}
